import { useCallback } from 'react';
import {
  ReportBackendTypes,
  AutoCompleteShipInfo,
  ShipInfo,
} from '@ais-ui/websockets';
import { reportTypesObject } from '../../Reports/reportsTypes';
import { useCreateReportContext } from '../../Reports/Mutations';
import { useSelectionState } from '../../../components/SelectionStateProvider';

/**
 * Hook for creating ship reports with pre-configured settings
 * Follows the same pattern as ReplayVideo component
 */
export const useCreateShipReport = () => {
  const { setChosenReportType, setShips, setShipAttribute, setFilter } =
    useCreateReportContext();
  const { targetInfoDetailsList } = useSelectionState();

  const createShipReport = useCallback(() => {
    // Set the report type to SHIPS
    setChosenReportType(reportTypesObject[ReportBackendTypes.SHIPS]);

    // Filter to only ship targets and create AutoCompleteShipInfo array for filter
    const shipTargets = targetInfoDetailsList
      .map((targetDetails) => targetDetails.extractedTargetInfo[0])
      .filter(
        (targetInfo): targetInfo is ShipInfo =>
          'mmsi' in targetInfo && 'name' in targetInfo,
      );

    // Create AutoCompleteShipInfo format for the filter step
    const filterShips: AutoCompleteShipInfo[] = shipTargets.map((shipInfo) => ({
      mmsi: shipInfo.mmsi?.toString() || null,
      name: shipInfo.name || null,
      callSign: shipInfo.callSign || null,
      imo: shipInfo.imo || null,
      type: shipInfo.shipType?.toString() || null,
      filteredOut: false,
      timedOut: false,
    }));

    // Create Ship format for the ships step (all strings required)
    const ships = shipTargets.map((shipInfo) => ({
      mmsi: shipInfo.mmsi?.toString() || '',
      name: shipInfo.name || `MMSI ${shipInfo.mmsi}`,
      callSign: shipInfo.callSign || '',
      imo: shipInfo.imo?.toString() || '',
      type: shipInfo.shipType?.toString() || '',
    }));

    // Set the ships in the filter (this is what the FilterStep expects)
    setFilter(filterShips);

    // Set the ships and configure to filter by ships
    setShips(ships);
    setShipAttribute('filterBy');
  }, [
    setChosenReportType,
    setShips,
    setShipAttribute,
    setFilter,
    targetInfoDetailsList,
  ]);

  return { createShipReport };
};
